import React, {
  ReactNode,
  createContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const SidebarControlsContext = createContext<{
  isExpanded: boolean;
  isToggleFooterVisible: boolean;
  shouldBeExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  shouldShowToggleFooter: (footerShouldBeVisible: boolean) => void;
}>({
  isExpanded: true,
  isToggleFooterVisible: true,
  // eslint-disable-next-line no-console
  shouldBeExpanded: (_shouldExpand) => console.log('not implemented'),
  shouldShowToggleFooter: (_footerShouldBeVisible) =>
    // eslint-disable-next-line no-console
    console.log('not implemented'),
});

interface Props {
  children: ReactNode;
}

export const SidebarControlsProvider = ({ children }: Props) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isToggleFooterVisible, setIsToggleFooterVisible] = useState(true);

  /**
   * If we hide the toggle we should make sure the content is visible
   */
  useEffect(() => {
    if (!isToggleFooterVisible && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isToggleFooterVisible, isExpanded]);

  const contextValue = useMemo(
    () => ({
      isExpanded,
      isToggleFooterVisible,
      shouldBeExpanded: setIsExpanded,
      shouldShowToggleFooter: setIsToggleFooterVisible,
    }),
    [
      isExpanded,
      isToggleFooterVisible,
      setIsExpanded,
      setIsToggleFooterVisible,
    ],
  );

  return (
    <SidebarControlsContext.Provider value={contextValue}>
      {children}
    </SidebarControlsContext.Provider>
  );
};

export const useSidebarControls = () => {
  const context = React.useContext(SidebarControlsContext);
  if (!context) {
    throw new Error(
      'useSidebarControls must be used within a SidebarControlsProvider',
    );
  }
  return context;
};
